import React, { Suspense, lazy, useEffect } from "react";
import { BrowserRouter as Router, Navigate, Route, Routes } from "react-router-dom";
import ProtectedRoute from "./routes/ProtectedRoute";
import Loader from "./components/Loader/Loader";
import { Toaster } from "./components/Toast/Toast";
import GlobalModals from "./components/GlobalModals/GlobalModals";
import { useRealtimeSync } from "./hooks/useRealtimeSync";
import { fetchUserConfig } from "./utils/roleHelper";
import { refreshTokenIfExpiringSoon } from "./core/api";
import { getValidate } from "./core/authValidate";
import { isTokenPresent } from "./core/auth";
import Login from "./pages/Login/Login";
// Usati da quasi tutte le pagine: stanno nel bundle principale, così il loro
// CSS non finisce in ordini diversi nei pezzi caricati al bisogno.
import "./components/Modal/ModalAlert";
import "./components/Segmented/Segmented";

const ResetPasswordPage = lazy(() => import("./pages/Login/ResetPasswordPage"));
const InvitoAzienda = lazy(() => import("./pages/Invito/InvitoAzienda"));
const PendingApproval = lazy(() => import("./pages/PendingApproval/PendingApproval"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe/Unsubscribe"));
const DisiscriviCliente = lazy(() => import("./pages/Unsubscribe/DisiscriviCliente"));
const NotFound = lazy(() => import("./pages/NotFound/NotFound"));
const PresenzeCliente = lazy(() => import("./pages/PresenzeCliente/PresenzeCliente"));
const Panoramica = lazy(() => import("./pages/Panoramica/Panoramica"));
const Clienti = lazy(() => import("./pages/Clienti/Clienti"));
const Dipendenti = lazy(() => import("./pages/Dipendenti/Dipendenti"));
const PresenzeHR = lazy(() => import("./pages/PresenzeHR/PresenzeHR"));
const Scadenze = lazy(() => import("./pages/Scadenze/Scadenze"));
const Importa = lazy(() => import("./pages/Importa/Importa"));
const GestisciUtenti = lazy(() => import("./pages/GestisciUtenti/GestisciUtenti"));
const GestisciRuoli = lazy(() => import("./pages/GestisciRuoli/GestisciRuoli"));
const GestisciProfilo = lazy(() => import("./pages/GestisciProfilo/GestisciProfilo"));
const GestisciAzienda = lazy(() => import("./pages/GestisciAzienda/GestisciAzienda"));
const GestioneAbbonamento = lazy(() => import("./pages/GestioneAbbonamento/GestioneAbbonamento"));
const BustePaga = lazy(() => import("./pages/BustePaga/BustePaga"));
const Configurazione = lazy(() => import("./pages/Configurazione/Configurazione"));
const AreaDipendente = lazy(() => import("./pages/AreaDipendente/AreaDipendente"));
const Kiosk = lazy(() => import("./pages/Kiosk/Kiosk"));

const P = (el) => <ProtectedRoute>{el}</ProtectedRoute>;

export default function App() {
  useRealtimeSync();

  // Controlli periodici (come gestionale-gls): rinnovo preventivo del token e
  // validazione della sessione, sospesi quando la scheda è nascosta.
  useEffect(() => {
    if (isTokenPresent()) {
      fetchUserConfig();
      refreshTokenIfExpiringSoon();
    }
    const tick = () => {
      if (document.hidden || !isTokenPresent()) return;
      refreshTokenIfExpiringSoon();
      getValidate().catch(() => {});
    };
    const id = setInterval(tick, 10 * 60 * 1000);
    const onVis = () => !document.hidden && tick();
    document.addEventListener("visibilitychange", onVis);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, []);

  return (
    <Router>
      <Toaster />
      <GlobalModals />
      <Suspense fallback={<Loader full />}>
        <Routes>
          <Route path="/Login" element={<Login />} />
          <Route path="/login" element={<Navigate to="/Login" replace />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/invito" element={<InvitoAzienda />} />
          <Route path="/invito/:token" element={<InvitoAzienda />} />
          <Route path="/invito/c/:code" element={<InvitoAzienda />} />
          <Route path="/pending-approval" element={<PendingApproval />} />
          <Route path="/unsubscribe" element={<Unsubscribe />} />
          {/* Pagine pubbliche dei clienti: niente login, basta il link. */}
          <Route path="/p/:token" element={<PresenzeCliente />} />
          {/* Area del dipendente (buste paga, timbratura, certificati) e tablet all'ingresso. */}
          <Route path="/d/:token" element={<AreaDipendente />} />
          <Route path="/k/:token" element={<Kiosk />} />
          <Route path="/disiscrivi-cliente" element={<DisiscriviCliente />} />

          <Route path="/" element={P(<Panoramica />)} />
          <Route path="/clienti" element={P(<Clienti />)} />
          <Route path="/dipendenti" element={P(<Dipendenti />)} />
          <Route path="/presenze-hr" element={P(<PresenzeHR />)} />
          <Route path="/scadenze" element={P(<Scadenze />)} />
          <Route path="/importa" element={P(<Importa />)} />
          <Route path="/buste-paga" element={P(<BustePaga />)} />
          <Route path="/configurazione" element={P(<Configurazione />)} />

          <Route path="/GestisciProfilo" element={P(<GestisciProfilo />)} />
          <Route path="/GestisciAzienda" element={P(<GestisciAzienda />)} />
          <Route path="/GestisciUtenti" element={P(<GestisciUtenti />)} />
          <Route path="/GestisciRuoli" element={P(<GestisciRuoli />)} />
          <Route path="/GestioneAbbonamento" element={P(<GestioneAbbonamento />)} />

          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </Router>
  );
}
